import { Popup, TPopupComponent } from '../../../../shared/components/popup/popup.abstract.tsx';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { TPopupDefaultActions } from '../../../../shared/components/popup/services/popup.service.ts';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { fontHelper } from '../../../../shared/helpers/font/font.helper.ts';
import { FormTextEditor, FormTextField } from '../../../../shared/components/form';
import { Button, Stack, Typography } from '@mui/material';
import { IOrderDetailsState as ICreateDetailsState } from '../../create/states/orderDetails/orderDetails.state.ts';
import { useBeforeClosePopup } from '../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';
import PushPinIcon from '@mui/icons-material/PushPin';
import { UnpinIcon } from '../../../../svg/custom.svg.tsx';

export class NotePopup extends Popup<INotePopup> {
  Component: FC<TPopupComponent>;
  isEditable?: boolean;
  showAdditionalNote: boolean;
  constructor(params: { isEditable?: boolean; showAdditionalNote: boolean }) {
    super();
    const { isEditable = true, showAdditionalNote } = params;
    const stream = this.innerStream;
    this.isEditable = isEditable;
    this.showAdditionalNote = showAdditionalNote;

    this.Component = () => {
      const Wrapper = useMemo(() => this.Wrapper, []);
      const { t } = useTranslation();
      const defaultFontSize = useMemo(() => fontHelper.getDefaultFont(['fontSize']), []);
      const [initValues, setInitValues] = useState<INotePopup['values']>({});
      const isNotePinned = initValues.isNotePinned;
      const { resetField, getValues } = useFormContext();
      const isPinnedButtonExists = typeof isNotePinned === 'boolean';
      useEffect(() => {
        stream.actionListener('open').subscribe(({ values }) => {
          setInitValues(values);
        });
      }, []);

      const getDirtyStatus = (): boolean => {
        const { note, note2 } = getValues();
        return (
          initValues.mainNote !== note ||
          (this.showAdditionalNote && initValues.additionalNote !== note2)
        );
      };

      useBeforeClosePopup({
        stream,
        isDirty: getDirtyStatus(),
        handlers: () => {
          resetField('note', { defaultValue: initValues.mainNote });
          resetField('note2', { defaultValue: initValues.additionalNote });
        },
        deps: [initValues.mainNote, initValues.additionalNote],
      });

      return (
        <Wrapper
          stream={stream}
          popupTitle={t('common.note')}
          slotProps={{ paper: { sx: { maxWidth: '750px' } } }}
          fullWidth
        >
          {initValues.plainTextNote && (
            <Fragment>
              <Typography
                variant='h5'
                sx={{
                  my: 2,
                }}
              >
                {t('order.bakery2b_comment')}
              </Typography>
              <FormTextField name='notePlainText' disabled width='100%' multiline />
              <Typography
                variant='h5'
                sx={{
                  my: 2,
                }}
              >
                {t('common.note')}
              </Typography>
            </Fragment>
          )}
          <FormTextEditor disabled={!this.isEditable} name='note' defaultFormat={defaultFontSize} />
          {this.showAdditionalNote && (
            <>
              <Typography
                variant='h5'
                sx={{
                  my: 2,
                }}
              >
                {t('order.additional_comments')}
              </Typography>
              <FormTextEditor
                disabled={!this.isEditable}
                name='note2'
                defaultFormat={defaultFontSize}
              />
            </>
          )}
          <Stack
            direction='row'
            spacing={2}
            sx={{
              mt: 3,
              justifyContent: isPinnedButtonExists ? 'space-between' : 'flex-end',
            }}
          >
            {isPinnedButtonExists && (
              <Button
                variant='outlined'
                onClick={() =>
                  setInitValues((prev) => {
                    const newIsNotePinned = !prev.isNotePinned;
                    stream.emit('updateIsNotePinned', {
                      values: { isNotePinned: newIsNotePinned },
                    });

                    return { ...prev, isNotePinned: newIsNotePinned };
                  })
                }
                startIcon={isNotePinned ? <UnpinIcon /> : <PushPinIcon />}
              >
                {isNotePinned ? t('order.unpin_from_main_screen') : t('order.pin_to_main_screen')}
              </Button>
            )}
            <Button
              onClick={() => stream.emit('close')}
              variant='contained'
              disabled={!this.isEditable}
            >
              {t('common.save')}
            </Button>
          </Stack>
        </Wrapper>
      );
    };
  }
}

interface INotePopup {
  action: TPopupDefaultActions | 'updateIsNotePinned';
  values: {
    plainTextNote?: ICreateDetailsState['orderData']['notePlainText'];
    mainNote?: ICreateDetailsState['orderData']['note'];
    additionalNote?: ICreateDetailsState['orderData']['note2'];
    isNotePinned?: boolean;
  };
}
